import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt } from "./reportUtils";

interface ServiceChargeReportsProps {
  period: string;
}

interface ServiceChargeData {
  unitName: string;
  totalCharge: number;
  paid: number;
  outstanding: number;
  percentPaid: number;
  paymentStatus: "paid" | "partial" | "overdue" | "unpaid";
  dueDate: string;
}

const ServiceChargeReports: React.FC<ServiceChargeReportsProps> = ({
  period,
}) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serviceChargeData, setServiceChargeData] = useState<
    ServiceChargeData[]
  >([]);
  const [totalCharged, setTotalCharged] = useState(0);
  const [totalCollected, setTotalCollected] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [collectionRate, setCollectionRate] = useState(0);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await reportsApi.getServiceChargeAllocation(
          currentBuilding.id,
          period,
        );
        if (cancelled) return;
        const charges = result?.data || result || [];
        setServiceChargeData(charges);
        const charged = charges.reduce(
          (sum: number, item: any) => sum + item.totalCharge,
          0,
        );
        const collected = charges.reduce(
          (sum: number, item: any) => sum + item.paid,
          0,
        );
        const outstanding = charges.reduce(
          (sum: number, item: any) => sum + item.outstanding,
          0,
        );
        setTotalCharged(charged);
        setTotalCollected(collected);
        setTotalOutstanding(outstanding);
        setCollectionRate(
          charged > 0 ? Math.round((collected / charged) * 100) : 0,
        );
      } catch (err) {
        console.error("Error fetching service charge report data:", err);
        if (!cancelled)
          setError(
            "Failed to load service charge reports. Please try again later.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading service charge reports..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Service Charge Reports
      </h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Charged
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {fmt(totalCharged)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Total Collected
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-green-600">
              {fmt(totalCollected)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Outstanding
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-red-600">
              {fmt(totalOutstanding)}
            </dd>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Collection Rate
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {collectionRate}%
            </dd>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
              <div
                className={`h-2.5 rounded-full ${
                  collectionRate < 70
                    ? "bg-red-600"
                    : collectionRate < 90
                    ? "bg-yellow-400"
                    : "bg-green-600"
                }`}
                style={{ width: `${collectionRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Charge Table */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Service Charge Collection
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Unit
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Total Charge
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Paid
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Outstanding
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serviceChargeData.map((unit) => (
                  <tr key={unit.unitName}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {unit.unitName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {fmt(unit.totalCharge)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">
                      {fmt(unit.paid)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 text-right">
                      {fmt(unit.outstanding)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          unit.paymentStatus === "paid"
                            ? "bg-green-100 text-green-800"
                            : unit.paymentStatus === "partial"
                            ? "bg-yellow-100 text-yellow-800"
                            : unit.paymentStatus === "overdue"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {unit.paymentStatus.charAt(0).toUpperCase() +
                          unit.paymentStatus.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Payment Visualization */}
          <div className="mt-8">
            <h4 className="text-sm font-medium text-gray-700 mb-4">
              Payment Progress by Unit
            </h4>
            <div className="space-y-4">
              {serviceChargeData.map((unit) => (
                <div key={unit.unitName} className="flex items-center">
                  <div className="w-24 text-sm text-gray-600">
                    {unit.unitName}
                  </div>
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full ${
                          unit.percentPaid === 100
                            ? "bg-green-600"
                            : unit.percentPaid >= 50
                            ? "bg-yellow-500"
                            : "bg-red-500"
                        }`}
                        style={{ width: `${unit.percentPaid}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-16 text-sm text-gray-600 text-right ml-2">
                    {unit.percentPaid}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceChargeReports;
